"use client";

import { FC, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useAtom, useAtomValue } from "jotai";
import { isEmpty } from "radash";
import { useSession } from "next-auth/react";
import type { Session } from "next-auth";
import { MdDelete, MdSend, MdSync } from "react-icons/md";
import classNames from "classnames";
import { Entry, CommentMessage } from "@/prisma-package/src";
import { $Enums } from "@/prisma-package/src";
import {
  EntryCommentsDrawerData,
  drawerAtom,
} from "@/app/_lib/atoms/drawer.atom";
import { usersPublicAtom } from "@/app/_lib/atoms/users.atom";
import {
  createCommentThreadAction,
  addCommentMessageAction,
  getCommentThreadsByTypeAction,
} from "@/app/_lib/actions/comment-thread.actions";
import { getUsersByOrgIdsAction } from "@/app/_lib/actions/user-roles.actions";
import { PageLoading } from "@/app/_lib/components/page-loading";
import ConfirmModal from "@/app/_lib/components/modals/confirm-modal";
import TextareaWithMentions from "@/app/_lib/components/forms/textarea-with-mentions";
import { useToast } from "@/app/_lib/hooks/use-toast";
import { UserPublic } from "@/app/_lib/types/user.types";
import { getUserNameDisplay } from "@/app/_lib/utils/user.utils";
import { getEstTime } from "@/app/_lib/utils/format-date-time.utils";

const CommentsDrawer: FC = () => {
  const drawer = useAtomValue(drawerAtom);
  const { data: session } = useSession();
  const ownUser = session?.user;

  if (!drawer.data) return null;
  if (!(drawer.data as EntryCommentsDrawerData).entry) return null;
  if (!ownUser) return null;

  const { entry, projectId, orgId } = drawer.data as EntryCommentsDrawerData;

  return (
    <CommentsContent
      entry={entry}
      projectId={projectId}
      ownUser={ownUser}
      orgId={orgId}
    />
  );
};

type CommentsContentProps = {
  entry: Entry;
  projectId: string;
  ownUser: NonNullable<Session["user"]>;
  orgId?: string | null;
};

const CommentsContent: FC<CommentsContentProps> = ({
  entry,
  projectId,
  ownUser,
  orgId,
}) => {
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [commentThread, setCommentThread] = useState<any>(null);
  const [comments, setComments] = useState<CommentMessage[]>([]);

  const [usersPublic, setUsersPublic] = useAtom(usersPublicAtom);

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Users are loaded one time on mount and then stored in the atom.
   * If users are already fetched, this effect will not run again.
   */
  useEffect(() => {
    if (isEmpty(usersPublic)) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadComments = async () => {
    // Get comment threads for ledger entries
    const [err, res] = await getCommentThreadsByTypeAction({
      type: $Enums.CommentThreadType.LEDGER,
      projectId,
      entityIds: [entry.id],
    });

    if (err || !res) {
      toast({
        title: "Failed to load comments",
        message: err?.message ?? "An unknown error occurred",
        type: "error",
        state: "active",
      });
      setIsLoading(false);
      return;
    }

    // Find the thread for this entry
    const entryThread = res.threads.find((thread: any) => thread.entityId === entry.id);

    if (entryThread) {
      setCommentThread(entryThread);
      setComments(entryThread.messages || []);
    } else {
      setCommentThread(null);
      setComments([]);
    }

    setIsLoading(false);
  };

  const loadUsers = async () => {
    const adminOrgId = process.env.NEXT_PUBLIC_ADMIN_ORG_ID;
    const orgIdsToFetch: string[] = [];

    // Always include the current user's org ID
    if (orgId) {
      orgIdsToFetch.push(orgId);
    }

    // Always include admin org ID if it exists
    if (adminOrgId && adminOrgId !== orgId) {
      orgIdsToFetch.push(adminOrgId);
    }

    // If we have no orgs to fetch, clear the users
    if (orgIdsToFetch.length === 0) {
      setUsersPublic({});
      return;
    }

    const [err, users] = await getUsersByOrgIdsAction({
      orgIds: orgIdsToFetch,
    });

    if (!err && users) {
      // Transform to match UserPublic interface expected by the atom
      const transformedUsers = users.reduce((acc, user) => {
        acc[user.id] = {
          id: user.id,
          email: user.email,
          firstName: user.email.split("@")[0], // fallback since we don't have firstName
          lastName: null, // fallback since we don't have lastName
          imageUrl: "", // fallback since we don't have imageUrl
          username: null, // fallback since we don't have username
        };
        return acc;
      }, {} as Record<string, any>);
      setUsersPublic(transformedUsers);
    } else {
      toast({
        title: "Failed to load users",
        message: err?.message ?? "An unknown error occurred",
        type: "error",
        state: "active",
      });
      setUsersPublic({});
    }
  };

  return (
    <div className="sm:min-w-[400px]">
      <h2 className="text-2xl pb-4 border-b border-gray-200 text-white">
        Comments for Entry #{entry.transactionNumber}
      </h2>

      <div className="mt-4">
        {isLoading ? (
          <PageLoading />
        ) : !comments?.length ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              Be the first to comment on this entry.
            </p>
            <CommentForm entry={entry} projectId={projectId} commentThread={commentThread} onNewComment={loadComments} />
          </div>
        ) : (
          <div>
            <div className="flex flex-col-reverse gap-1 h-[calc(100vh-313px)] overflow-y-auto">
              {comments.map((comment) => {
                const currentUser =
                  comment.userId === ownUser.id
                    ? ownUser
                    : usersPublic[comment.userId];

                return (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    currentUser={currentUser}
                    isOwnComment={comment.userId === ownUser.id}
                    onDeleteComment={loadComments}
                  />
                );
              })}
            </div>
            <div className="divider"></div>
            <CommentForm entry={entry} projectId={projectId} commentThread={commentThread} onNewComment={loadComments} />
          </div>
        )}
      </div>
    </div>
  );
};

type CommentProps = {
  comment: CommentMessage;
  currentUser?: UserPublic | NonNullable<Session["user"]>;
  isOwnComment: boolean;
  onDeleteComment: () => Promise<void>;
};

const Comment: FC<CommentProps> = ({
  comment,
  currentUser,
  isOwnComment,
  onDeleteComment,
}) => {
  const toast = useToast();

  const usersPublic = useAtomValue(usersPublicAtom);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const username = isOwnComment ? "Me" : getUserNameDisplay(currentUser);
  const commentDate = new Date(comment.createdAt);

  // Handle date display
  const dateDisplay = (() => {
    const now = new Date();
    const isToday = commentDate.toDateString() === now.toDateString();
    const isYesterday =
      new Date(now.setDate(now.getDate() - 1)).toDateString() ===
      commentDate.toDateString();

    if (isToday) {
      return getEstTime(commentDate, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    if (isYesterday) {
      return "yesterday";
    }

    const diffTime = Math.abs(now.getTime() - commentDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days ago`;
  })();

  // Handle mention displays in comment
  const commentContent = useMemo(() => {
    // For now, just display the content as-is
    // TODO: If we need mention display, we'd need to store mentionedUserIds in CommentMessage
    // or parse mentions from the content
    return comment.content;
  }, [comment.content]);

  const handleDeleteComment = async () => {
    // TODO: Implement comment message deletion in comment thread system
    toast({
      title: "Not implemented",
      message: "Comment deletion is not yet implemented for the thread system.",
      type: "error",
      state: "active",
    });
  };

  return (
    <>
      <div
        className={classNames("chat", isOwnComment ? "chat-end" : "chat-start")}
      >
        <div className="chat-image avatar">
          <div className="w-10 rounded-full">
            <img
              alt="Tailwind CSS chat bubble component"
              src={
                (currentUser && "imageUrl" in currentUser
                  ? currentUser.imageUrl
                  : currentUser && "image" in currentUser
                    ? currentUser.image
                    : null) ||
                "https://img.daisyui.com/images/stock/photo-1534528741775-53994a69daeb.webp"
              }
            />
          </div>
        </div>
        <div className="chat-header gap-4 mb-1">
          {username}
          <div
            className="inline-block lg:tooltip"
            data-tip={getEstTime(commentDate)}
          >
            <time className="pl-2 text-xs opacity-50">{dateDisplay}</time>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 group">
          {isOwnComment && (
            <button
              className="opacity-0 transition-opacity group-hover:opacity-100"
              onClick={() => setIsConfirmDeleteOpen(true)}
            >
              <MdDelete className="text-error text-xl" />
            </button>
          )}
          <div
            className={classNames("chat-bubble max-w-[400px]", {
              "chat-bubble-primary": isOwnComment,
            })}
          >
            {commentContent}
          </div>
        </div>
      </div>
      <ConfirmModal
        title="Confirm Comment Deletion"
        content="Are you sure you want to delete this comment?"
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleDeleteComment}
      />
    </>
  );
};

type CommentFormProps = {
  entry: Entry;
  projectId: string;
  commentThread: any | null;
  onNewComment: () => Promise<void>;
};

const CommentForm: FC<CommentFormProps> = ({ entry, projectId, commentThread, onNewComment }) => {
  const toast = useToast();
  const router = useRouter();

  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState<UserPublic[]>([]);

  const usersPublic = useAtomValue(usersPublicAtom);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const sanitizedComment = comment.trim();
    if (!sanitizedComment) return;

    setIsLoading(true);

    try {
      if (!commentThread) {
        // Create new comment thread for this entry
        const [threadErr, threadRes] = await createCommentThreadAction({
          type: $Enums.CommentThreadType.LEDGER,
          title: `Entry #${entry.transactionNumber}`,
          entityId: entry.id,
          projectId,
          initialMessage: sanitizedComment,
          subscribers: mentionedUsers.map((user) => user.id),
        });

        if (threadErr || !threadRes) {
          toast({
            title: "Failed to create comment thread",
            message: threadErr?.message ?? "An unknown error occurred",
            type: "error",
            state: "active",
          });
          setIsLoading(false);
          return;
        }
      } else {
        // Add message to existing thread
        const [messageErr, messageRes] = await addCommentMessageAction({
          threadId: commentThread.id,
          projectId,
          content: sanitizedComment,
          mentionedUserIds: mentionedUsers.map((user) => user.id),
        });

        if (messageErr || !messageRes) {
          toast({
            title: "Failed to post comment",
            message: messageErr?.message ?? "An unknown error occurred",
            type: "error",
            state: "active",
          });
          setIsLoading(false);
          return;
        }
      }

      setComment("");
      setMentionedUsers([]);
      router.refresh();
      onNewComment();
    } catch (error) {
      toast({
        title: "Failed to post comment",
        message: "An unexpected error occurred",
        type: "error",
        state: "active",
      });
    }

    setIsLoading(false);
  };

  return (
    <form
      className="flex flex-col gap-2 w-full relative"
      onSubmit={handleSubmit}
    >
      <TextareaWithMentions
        label=""
        name="comment"
        placeholder="Enter comment"
        usersPublic={usersPublic}
        value={comment}
        onChange={setComment}
        onMentionedUsersChange={setMentionedUsers}
      />
      <button className="btn btn-md btn-primary" disabled={!comment}>
        {isLoading ? <MdSync className="animate-spin" /> : <MdSend />}
        {isLoading ? "Posting..." : "Post Comment"}
      </button>
    </form>
  );
};

export default CommentsDrawer;
